import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
} from '@mui/material';
import { usePreModifyReviewPolicyMutation, useModifyReviewPolicyMutation } from '../../../../api/preHQ';
import { useSnackbar } from 'notistack';

const ReviewerAndApproverModal = ({ open, onClose, positionReviewId }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [preModifyChangeRequestPolicy, { data: reviewPolicyData, isLoading: isPreModifyLoading }] =
        usePreModifyReviewPolicyMutation();
    const [modifyChangeRequestPolicy, { isLoading: isModifyLoading }] = useModifyReviewPolicyMutation();

    const [selectedReviewer, setSelectedReviewer] = useState('');
    const [selectedApprover, setSelectedApprover] = useState('');

    const { approvers = [], reviewers = [], policy: changeRequestPolicy = {} } = reviewPolicyData || {};

    useEffect(() => {
        if (!positionReviewId) return;
        const data = new FormData();
        data.append('id', Number(positionReviewId));
        try {
            preModifyChangeRequestPolicy(data).unwrap();
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positionReviewId]);

    // Set initial values when reviewPolicyData changes
    useEffect(() => {
        if (changeRequestPolicy) {
            setSelectedReviewer(changeRequestPolicy.reviewer || '');
            setSelectedApprover(changeRequestPolicy.approver || '');
        }
    }, [changeRequestPolicy]);
    const handleCancel = () => {
        onClose?.(false);
    };

    const handleConfirm = async () => {
        if (!selectedReviewer || !selectedApprover) {
            enqueueSnackbar('Please select both reviewer and approver', { variant: 'error' });
            return;
        }

        // Pass the selected reviewer and approver back to the parent
        // onClose?.({
        //     reviewerId: selectedReviewer,
        //     approverId: selectedApprover,
        // });
        const { name, status } = changeRequestPolicy;

        if (!!name && !!status) {
            const payload = {
                id: positionReviewId,
                name,
                status,
            };
            payload['reviewerId'] = selectedReviewer || changeRequestPolicy.reviewerId;
            payload['approverId'] = selectedApprover || changeRequestPolicy.approverId;
            try {
                const response = await modifyChangeRequestPolicy(payload).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                enqueueSnackbar(response?.message, { variant: 'success' });
                onClose?.(true); // Pass success flag back to parent
            } catch (error) {
                if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
                else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
            }
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="sm"
            PaperProps={{
                sx: {
                    width: '600px',
                    maxWidth: '90vw',
                },
            }}
        >
            <DialogTitle>Review Policy</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                            Policy Reviewer
                        </Typography>
                        <FormControl fullWidth size="small">
                            <InputLabel id="reviewer-label">Select Reviewer</InputLabel>
                            <Select
                                labelId="reviewer-label"
                                value={selectedReviewer}
                                label="Select Reviewer"
                                onChange={(e) => setSelectedReviewer(e.target.value)}
                            >
                                {reviewers?.map((reviewer) => (
                                    <MenuItem key={reviewer.id} value={reviewer.id}>
                                        {reviewer.fullNameWithRole}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                            Policy Approver
                        </Typography>
                        <FormControl fullWidth size="small">
                            <InputLabel id="approver-label">Select Approver</InputLabel>
                            <Select
                                labelId="approver-label"
                                value={selectedApprover}
                                label="Select Approver"
                                onChange={(e) => setSelectedApprover(e.target.value)}
                            >
                                {approvers?.map((approver) => (
                                    <MenuItem key={approver.id} value={approver.id}>
                                        {approver.fullNameWithRole}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button onClick={handleConfirm} variant="contained" color="primary">
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReviewerAndApproverModal;
